import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import './AdvertiserSignup.css';
import '../AdvertiserActivate/AdvertiserActivate.css';
import { getAdvertiserSignupCategories } from '../../../services/getAdvertiserSignupCategories';
import { advertiserSignupSendCode } from '../../../services/advertiserSignupSendCode';
import { advertiserSignupVerifyCode } from '../../../services/advertiserSignupVerifyCode';
import { advertiserSignup } from '../../../services/advertiserSignup';
import { save } from '../../../features/token/tokenSaver';
import { persistRoleSession } from '../../../services/authSession';
import { usePlatformAdvertisingAgreement } from '../../../hooks/usePlatformAdvertisingAgreement';
import InvertAdvertisingAgreementDocument from '../../../components/InvertAdvertisingAgreementDocument/InvertAdvertisingAgreementDocument';
import '../../../components/InvertAdvertisingAgreementDocument/InvertAdvertisingAgreementDocument.css';
import { platformAgreementPath } from '../../../utils/platformAgreementUrl';
import posthog from '../../../services/posthog';
import ScreenInstallCarousel from './ScreenInstallCarousel';
import SegmentedOtpInput from './SegmentedOtpInput';
import { useAffiliateLink } from '../../../hooks/useAffiliateLink';

const PROGRESS_LABELS = ['Welcome', 'Business', 'Contact', 'Verify', 'Password', 'Agreement'];
const TOTAL_STEPS = PROGRESS_LABELS.length;

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

function SignupNav({ onLogoClick }) {
    return (
        <nav className="advertiser-activate-nav">
            <button type="button" className="advertiser-activate-nav-brand" onClick={onLogoClick}>
                <span className="advertiser-activate-nav-wordmark">INVERT</span>
            </button>
        </nav>
    );
}

export default function AdvertiserSignup() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);
    const [emailVerificationToken, setEmailVerificationToken] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [form, setForm] = useState({
        companyName: '',
        fname: '',
        lname: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
    });

    const { doc: agreementDoc } = usePlatformAdvertisingAgreement(500);
    const tosVersion = agreementDoc?.version;
    const agreementLink = platformAgreementPath(500);
    const affiliateCode = useAffiliateLink();
    const affiliateAnalytics = useMemo(
        () => (affiliateCode ? { affiliateCode } : {}),
        [affiliateCode]
    );

    useEffect(() => {
        if (currentStep === 3 && !codeSent && !emailVerified && !sendingCode && form.email.trim()) {
            handleSendCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    useEffect(() => {
        posthog.capture('advertiser signup started', affiliateAnalytics);
        getAdvertiserSignupCategories()
            .then((res) => {
                const options = (res.data?.categories || []).map((c) => ({
                    label: c.CategoryName,
                    value: c.CategoryId,
                }));
                setCategories(options);
            })
            .catch(() => setCategories([]));
    }, [affiliateAnalytics]);

    function onChange(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'email') {
            setEmailVerified(false);
            setEmailVerificationToken(null);
            setCodeSent(false);
            setOtpCode('');
        }
    }

    function validateStep(step) {
        if (step === 1) {
            if (!form.companyName.trim()) return 'Company name is required';
            if (!selectedCategory) return 'Please select a business category';
        }
        if (step === 2) {
            if (!form.fname.trim() || !form.lname.trim()) return 'First and last name are required';
            if (!form.email.trim() || !EMAIL_REGEX.test(form.email.trim())) return 'Valid email is required';
        }
        if (step === 3) {
            if (!emailVerified || !emailVerificationToken) return 'Please verify your email before continuing';
        }
        if (step === 4) {
            if (!form.password || form.password.length < 8) return 'Password must be at least 8 characters';
            if (form.password !== form.confirmPassword) return 'Passwords do not match';
        }
        if (step === 5) {
            if (!tosAccepted) return 'You must accept the INVERT Advertising Agreement';
            if (!tosVersion) return 'Could not load agreement version. Please refresh.';
        }
        return '';
    }

    function goNext() {
        const msg = validateStep(currentStep);
        if (msg) {
            setError(msg);
            return;
        }
        setError('');
        setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
    }

    function goBack() {
        setError('');
        setCurrentStep((s) => Math.max(s - 1, 0));
    }

    async function handleSendCode() {
        const email = form.email.trim();
        if (!email || !EMAIL_REGEX.test(email)) {
            setError('Enter a valid email on the previous step');
            return;
        }
        setError('');
        setSendingCode(true);
        try {
            await advertiserSignupSendCode(email);
            setCodeSent(true);
            posthog.capture('advertiser signup email code sent', { email, ...affiliateAnalytics });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not send verification code');
        } finally {
            setSendingCode(false);
        }
    }

    async function handleVerifyCode() {
        const email = form.email.trim();
        if (!/^\d{6}$/.test(otpCode.trim())) {
            setError('Enter the 6-digit code from your email');
            return;
        }
        setError('');
        setVerifyingCode(true);
        try {
            const res = await advertiserSignupVerifyCode(email, otpCode.trim());
            setEmailVerificationToken(res.data.emailVerificationToken);
            setEmailVerified(true);
            posthog.capture('advertiser signup email verified', { email, ...affiliateAnalytics });
        } catch (err) {
            setError(err.response?.data?.error || 'Verification failed');
        } finally {
            setVerifyingCode(false);
        }
    }

    async function handleSubmit() {
        const msg = validateStep(5);
        if (msg) {
            setError(msg);
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const res = await advertiserSignup({
                emailVerificationToken,
                email: form.email.trim(),
                password: form.password,
                confirmPassword: form.confirmPassword,
                fname: form.fname.trim(),
                lname: form.lname.trim(),
                companyName: form.companyName.trim(),
                phoneNumber: form.phoneNumber.trim() || undefined,
                categoryId: selectedCategory.value,
                tosAccepted: true,
                tosVersion,
            });
            const data = res.data;
            const fullName = `${data.fname} ${data.lname}`.trim();
            posthog.identify(form.email.trim(), {
                email: form.email.trim(),
                name: fullName,
                role: 'advertiser',
            });
            posthog.capture('advertiser signup completed', { email: form.email.trim(), ...affiliateAnalytics });
            dispatch(save(data.token));
            persistRoleSession({
                role: 'advertiser',
                token: data.token,
                refreshToken: data.refreshToken,
                name: fullName,
            });
            window.location.href = '/advertiserDashboard/home?onboarding=1';
        } catch (err) {
            const apiErr = err.response?.data;
            setError(apiErr?.error || 'Signup failed. Please try again.');
            if (apiErr?.currentTosVersion) {
                setError('Agreement was updated. Please review and accept again.');
            }
            setSubmitting(false);
        }
    }

    function renderStepContent() {
        switch (currentStep) {
            case 0:
                return (
                    <>
                        <span className="advertiser-activate-title">Advertise on INVERT</span>
                        <span className="advertiser-activate-subtitle">
                            Create your account in a few minutes and advertise on real screens in local venues across our
                            network.
                        </span>
                        <ScreenInstallCarousel />
                        <p className="advertiser-signup-carousel-caption">
                            Real INVERT installations on the network—retail, dining, and neighborhood spots where your
                            campaigns run.
                        </p>
                    </>
                );
            case 1:
                return (
                    <>
                        <span className="advertiser-activate-title">Your business</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-company">Company name</label>
                            <input
                                id="signup-company"
                                type="text"
                                required
                                value={form.companyName}
                                onChange={(e) => onChange('companyName', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label>Business category</label>
                            <Select
                                options={categories}
                                value={selectedCategory}
                                onChange={(opt) => setSelectedCategory(opt)}
                                placeholder="Select category…"
                            />
                        </div>
                    </>
                );
            case 2:
                return (
                    <>
                        <span className="advertiser-activate-title">Contact details</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-fname">First name</label>
                            <input
                                id="signup-fname"
                                type="text"
                                required
                                value={form.fname}
                                onChange={(e) => onChange('fname', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-lname">Last name</label>
                            <input
                                id="signup-lname"
                                type="text"
                                required
                                value={form.lname}
                                onChange={(e) => onChange('lname', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-email">Email</label>
                            <input
                                id="signup-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => onChange('email', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-phone">Phone (optional)</label>
                            <input
                                id="signup-phone"
                                type="tel"
                                value={form.phoneNumber}
                                onChange={(e) => onChange('phoneNumber', e.target.value)}
                            />
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <span className="advertiser-activate-title">Verify your email</span>
                        <span className="advertiser-activate-subtitle">
                            We sent a 6-digit code to{' '}
                            <strong className="advertiser-signup-email-highlight">{form.email.trim()}</strong>
                        </span>
                        {!codeSent ? (
                            <div className="advertiser-signup-send-code-wrap">
                                <button
                                    type="button"
                                    className="advertiser-activate-submit"
                                    onClick={handleSendCode}
                                    disabled={sendingCode}
                                >
                                    {sendingCode ? 'Sending…' : 'Send verification code'}
                                </button>
                            </div>
                        ) : (
                            <>
                                {emailVerified ? (
                                    <p className="advertiser-signup-verified-badge">Email verified</p>
                                ) : (
                                    <div className="advertiser-signup-otp-block">
                                        <SegmentedOtpInput
                                            value={otpCode}
                                            onChange={setOtpCode}
                                            disabled={verifyingCode}
                                            autoFocus={codeSent}
                                        />
                                        <div className="advertiser-signup-otp-actions">
                                            <button
                                                type="button"
                                                className="advertiser-activate-submit"
                                                onClick={handleVerifyCode}
                                                disabled={verifyingCode || otpCode.length < 6}
                                            >
                                                {verifyingCode ? 'Checking…' : 'Verify'}
                                            </button>
                                            <button
                                                type="button"
                                                className="advertiser-signup-btn-secondary"
                                                onClick={handleSendCode}
                                                disabled={sendingCode}
                                            >
                                                Resend code
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                );
            case 4:
                return (
                    <>
                        <span className="advertiser-activate-title">Create a password</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-password">Password</label>
                            <input
                                id="signup-password"
                                type="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={(e) => onChange('password', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="signup-confirm">Confirm password</label>
                            <input
                                id="signup-confirm"
                                type="password"
                                required
                                minLength={8}
                                value={form.confirmPassword}
                                onChange={(e) => onChange('confirmPassword', e.target.value)}
                            />
                        </div>
                    </>
                );
            case 5:
                return (
                    <>
                        <span className="advertiser-activate-title">Advertising agreement</span>
                        <div className="invert-ad-agreement-scroll-panel invert-ad-agreement-scroll-panel--tall">
                            <InvertAdvertisingAgreementDocument
                                amountUsd={500}
                                compact
                                showSignatures
                                companyName={form.companyName}
                                legalName={`${form.fname} ${form.lname}`.trim()}
                            />
                        </div>
                        <label className="advertiser-activate-checkbox">
                            <input
                                type="checkbox"
                                checked={tosAccepted}
                                onChange={(e) => {
                                    setTosAccepted(e.target.checked);
                                    if (e.target.checked) setError('');
                                }}
                            />
                            <span>
                                I agree to the{' '}
                                <Link to={agreementLink} target="_blank" rel="noopener noreferrer">
                                    INVERT Advertising Agreement
                                </Link>
                                {tosVersion ? ` (version ${tosVersion})` : ''}
                            </span>
                        </label>
                    </>
                );
            default:
                return null;
        }
    }

    return (
        <div className="advertiser-activate-page advertiser-signup-page">
            <SignupNav onLogoClick={() => navigate('/')} />
            <div className="advertiser-signup-steps">
                <div className="advertiser-signup-steps-progress">
                    {PROGRESS_LABELS.map((_, i) => (
                        <div
                            key={PROGRESS_LABELS[i]}
                            className={`advertiser-signup-step-bar ${i <= currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
                            title={PROGRESS_LABELS[i]}
                        />
                    ))}
                </div>
            </div>
            <div className="advertiser-activate-main">
                <div className="advertiser-activate-card">
                    {renderStepContent()}
                    {error && <p className="advertiser-activate-error">{error}</p>}
                    <div className="advertiser-signup-actions">
                        {currentStep > 0 && (
                            <button type="button" className="advertiser-signup-btn-secondary" onClick={goBack}>
                                Back
                            </button>
                        )}
                        {currentStep < TOTAL_STEPS - 1 ? (
                            <button
                                type="button"
                                className="advertiser-activate-submit"
                                onClick={() => {
                                    if (currentStep === 3 && !emailVerified) {
                                        const v = validateStep(3);
                                        if (v) {
                                            setError(v);
                                            return;
                                        }
                                    }
                                    goNext();
                                }}
                            >
                                Continue
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="advertiser-activate-submit"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Creating account…' : 'Create account'}
                            </button>
                        )}
                    </div>
                    <p className="advertiser-signup-footer-link">
                        Already have an account?{' '}
                        <button type="button" className="advertiser-activate-tos-link" onClick={() => navigate('/advertiserLogin/')}>
                            Log in
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
